import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { KNOWLEDGE_CLUSTERS, TRUST_SLUGS } from '../utils/knowledgeClusters';
import {
  BookOpen, Search, Clock, ChevronRight, Tag as TagIcon, ArrowRight, Calculator, Package, Award,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS_SV = {
  guide: 'Guider',
  'how-to': 'Steg-för-steg',
  news: 'Nyheter',
  faq: 'Vanliga frågor',
};

const ArticleCard = ({ article, language, idx }) => (
  <Link
    to={`/kunskap/${article.slug}`}
    className="group bg-white border border-stone-200 hover:shadow-md transition-all flex flex-col"
    data-testid={`article-card-${idx}`}
  >
    <div className="aspect-video bg-stone-100 overflow-hidden">
      {article.cover_image_url ? (
        <img
          src={article.cover_image_url}
          alt={article.title_sv}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-stone-300">
          <BookOpen className="w-10 h-10" />
        </div>
      )}
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 mb-2">
        <span>{CATEGORY_LABELS_SV[article.category] || article.category}</span>
        <span>·</span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {article.reading_time_minutes || 5} min
        </span>
      </div>
      <h4 className="text-lg font-semibold text-stone-900 group-hover:text-[#321a27] transition-colors mb-2 line-clamp-2">
        {language === 'sv' ? article.title_sv : (article.title_en || article.title_sv)}
      </h4>
      <p className="text-sm text-stone-600 line-clamp-3 mb-4 flex-1">
        {language === 'sv' ? article.excerpt_sv : (article.excerpt_en || article.excerpt_sv)}
      </p>
      {(article.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {article.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] px-2 py-0.5 bg-stone-100 text-stone-600 inline-flex items-center gap-1">
              <TagIcon className="w-2.5 h-2.5" /> {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  </Link>
);

const KnowledgeHubPage = () => {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCategory) params.set('category', activeCategory);
        const [articlesRes, catsRes] = await Promise.all([
          axios.get(`${API}/articles?${params.toString()}`),
          axios.get(`${API}/articles/categories/list`),
        ]);
        setArticles(articlesRes.data || []);
        setCategoryStats(catsRes.data || []);
      } catch (err) {
        // Silently fail - empty state will render
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeCategory]);

  const filteredArticles = articles.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const t = language === 'sv' ? a.title_sv : a.title_en;
    const e = language === 'sv' ? a.excerpt_sv : a.excerpt_en;
    return (t || '').toLowerCase().includes(q) || (e || '').toLowerCase().includes(q);
  });

  const bySlug = Object.fromEntries(articles.map((a) => [a.slug, a]));
  const clusteredSlugs = new Set([...KNOWLEDGE_CLUSTERS.flatMap((c) => c.slugs), ...TRUST_SLUGS]);
  const unclustered = articles.filter((a) => !clusteredSlugs.has(a.slug));
  const trustArticles = TRUST_SLUGS.map((s) => bySlug[s]).filter(Boolean);
  const isPillarView = !search && !activeCategory;

  const breadcrumbs = [
    { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se/' },
    { name: language === 'sv' ? 'Kunskap' : 'Knowledge', url: 'https://flowingo.se/kunskap' },
  ];

  return (
    <div className="bg-[#FAFAF8] min-h-screen pt-24" data-testid="knowledge-hub-page">
      <SEO
        title={language === 'sv'
          ? 'Dimensionering av VVS-system: komplett kunskapshub för proffs'
          : 'HVAC system sizing: complete knowledge hub for pros'}
        description={language === 'sv'
          ? 'Komplett guide till dimensionering av VVS-system: expansionskärl, ackumulatortankar, värmeväxlare, injustering, tappvatten och återströmningsskydd. Tekniska guider och kostnadsfria kalkylatorer.'
          : 'Complete guide to HVAC system sizing: expansion vessels, buffer tanks, heat exchangers, balancing, potable water and backflow prevention.'}
        type="website"
        language={language}
        schema={[generateBreadcrumbSchema(breadcrumbs)]}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#321a27] to-[#4a2d3d] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4 opacity-80">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm uppercase tracking-widest">
              {language === 'sv' ? 'Kunskapshub' : 'Knowledge Hub'}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
            {language === 'sv'
              ? 'Dimensionering av VVS-system – komplett guide för installatörer och projektörer'
              : 'HVAC system sizing – the complete guide for professionals'}
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-3xl">
            {language === 'sv'
              ? 'Från expansionskärl och ackumulatortankar till injustering och återströmningsskydd. Tekniska djupguider med formler, standarder och räkneexempel – plus kostnadsfria kalkylatorer. Skrivet av branschspecialister.'
              : 'From expansion vessels and buffer tanks to balancing and backflow prevention. Deep technical guides with formulas, standards and worked examples – plus free calculators.'}
          </p>

          {/* Search bar */}
          <div className="mt-10 max-w-xl relative">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'sv' ? 'Sök bland artiklar...' : 'Search articles...'}
              className="pl-12 h-14 text-base bg-white text-stone-900 rounded-none border-0 placeholder:text-stone-400"
              data-testid="knowledge-search"
            />
          </div>
        </div>
      </section>

      {/* Category filters */}
      <section className="border-b border-stone-200 bg-white sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto">
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('category');
              setSearchParams(next);
            }}
            data-testid="cat-filter-all"
            className={`px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
              !activeCategory ? 'bg-[#321a27] text-white' : 'text-stone-600 hover:bg-stone-100'
            }`}
          >
            {language === 'sv' ? 'Alla' : 'All'} ({articles.length})
          </button>
          {categoryStats.map((c) => (
            <button
              key={c.category}
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.set('category', c.category);
                setSearchParams(next);
              }}
              data-testid={`cat-filter-${c.category}`}
              className={`px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                activeCategory === c.category ? 'bg-[#321a27] text-white' : 'text-stone-600 hover:bg-stone-100'
              }`}
            >
              {CATEGORY_LABELS_SV[c.category] || c.category} ({c.count})
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white p-6 animate-pulse">
                <div className="h-40 bg-stone-200 mb-4" />
                <div className="h-3 bg-stone-200 w-1/3 mb-2" />
                <div className="h-5 bg-stone-200 w-4/5 mb-3" />
                <div className="h-3 bg-stone-100 w-full mb-1" />
                <div className="h-3 bg-stone-100 w-3/4" />
              </div>
            ))}
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="bg-white border border-stone-200 p-12 text-center">
            <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-stone-900 mb-2">
              {language === 'sv' ? 'Inga artiklar hittades' : 'No articles found'}
            </h2>
            <p className="text-stone-500">
              {language === 'sv' ? 'Vi arbetar på fler guider — kom tillbaka snart.' : 'More guides coming soon.'}
            </p>
          </div>
        ) : isPillarView ? (
          <>
            {/* Pillar intro */}
            <div className="max-w-3xl mb-14" data-testid="pillar-intro">
              <p className="text-stone-700 leading-relaxed mb-4">
                Att dimensionera ett VVS-system handlar om att få varje delsystem att samspela: expansionskärlet
                ska klara vattnets volymökning utan att säkerhetsventilen lyfter, ackumulatortanken ska jämna ut
                effekttoppar, värmeväxlaren ska överföra rätt effekt vid rätt temperaturprogram, och reglerventilerna
                ska ha tillräcklig auktoritet för att systemet ska gå att styra. Räknar du fel i ett led fortplantar
                sig problemet genom hela anläggningen – i form av tryckslag, pendlande reglering, legionellarisk
                eller onödigt hög energianvändning.
              </p>
              <p className="text-stone-700 leading-relaxed">
                Den här kunskapshubben samlar våra tekniska djupguider i sex ämnesområden, alla skrivna utifrån
                gällande standarder som SS-EN 12828, SS-EN 1717 och branschpraxis. Till flera områden finns
                kostnadsfria kalkylatorer som gör beräkningen åt dig – och varje guide länkar vidare till de
                produktkategorier du behöver när dimensioneringen är klar.
              </p>
            </div>

            {/* Clusters */}
            <div className="space-y-16">
              {KNOWLEDGE_CLUSTERS.map((cluster) => {
                const clusterArticles = cluster.slugs.map((s) => bySlug[s]).filter(Boolean);
                if (clusterArticles.length === 0) return null;
                return (
                  <section key={cluster.id} data-testid={`cluster-${cluster.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6 border-b border-stone-200 pb-4">
                      <div className="max-w-2xl">
                        <h2 className="text-2xl font-bold text-stone-900 mb-2">{cluster.title}</h2>
                        <p className="text-sm text-stone-600 leading-relaxed">{cluster.description}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        {cluster.calculators.map((calc) => (
                          <Link
                            key={calc.href}
                            to={calc.href}
                            className="inline-flex items-center gap-2 text-xs font-medium bg-[#321a27] text-white px-3 py-2 hover:bg-[#4a2d3d] transition-colors"
                            data-testid={`cluster-calc-${cluster.id}`}
                          >
                            <Calculator className="w-3.5 h-3.5" /> {calc.name}
                          </Link>
                        ))}
                        <Link
                          to={cluster.category.href}
                          className="inline-flex items-center gap-2 text-xs font-medium border border-stone-300 text-stone-700 px-3 py-2 hover:border-[#321a27] hover:text-[#321a27] transition-colors"
                        >
                          <Package className="w-3.5 h-3.5" /> {cluster.category.name}
                        </Link>
                        {(cluster.extraCategories || []).map((ec) => (
                          <Link
                            key={ec.href}
                            to={ec.href}
                            className="inline-flex items-center gap-2 text-xs font-medium border border-stone-300 text-stone-700 px-3 py-2 hover:border-[#321a27] hover:text-[#321a27] transition-colors"
                          >
                            <Package className="w-3.5 h-3.5" /> {ec.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {clusterArticles.map((a, idx) => (
                        <ArticleCard key={a.slug} article={a} language={language} idx={`${cluster.id}-${idx}`} />
                      ))}
                    </div>
                  </section>
                );
              })}

              {/* Trust / research section */}
              {trustArticles.length > 0 && (
                <section data-testid="cluster-trust">
                  <div className="mb-6 border-b border-stone-200 pb-4 max-w-2xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-5 h-5 text-[#321a27]" />
                      <h2 className="text-2xl font-bold text-stone-900">Forskning & fördjupning</h2>
                    </div>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      Bredare läsning om vad forskning och verkliga haverier lär oss om VVS-tekniska vägval.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {trustArticles.map((a, idx) => (
                      <ArticleCard key={a.slug} article={a} language={language} idx={`trust-${idx}`} />
                    ))}
                  </div>
                </section>
              )}

              {/* Any articles not yet mapped to a cluster */}
              {unclustered.length > 0 && (
                <section data-testid="cluster-other">
                  <div className="mb-6 border-b border-stone-200 pb-4">
                    <h2 className="text-2xl font-bold text-stone-900">Fler artiklar</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {unclustered.map((a, idx) => (
                      <ArticleCard key={a.slug} article={a} language={language} idx={`other-${idx}`} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article, idx) => (
              <ArticleCard key={article.slug} article={article} language={language} idx={idx} />
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 bg-white border border-stone-200 p-10 text-center">
          <h2 className="text-2xl font-bold text-stone-900 mb-3">
            {language === 'sv' ? 'Har du tekniska frågor?' : 'Got technical questions?'}
          </h2>
          <p className="text-stone-600 max-w-xl mx-auto mb-6">
            {language === 'sv'
              ? 'Våra ingenjörer hjälper dig dimensionera tankar, välja rätt komponenter och svara på alla VVS-frågor — gratis konsultation.'
              : 'Our engineers help you size tanks, pick the right components and answer all your HVAC questions — free consultation.'}
          </p>
          <Link to="/kontakt">
            <Button className="bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none">
              {language === 'sv' ? 'Boka kostnadsfri konsultation' : 'Book free consultation'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeHubPage;
